import { user } from "../libs/api-url";
import axios from "@/libs/axios";

/**
 * User Master Service
 * Handles all user management API calls based on the backend API documentation.
 * 
 * API Base URL: /api/v1/admin/user
 * Endpoints:
 * - GET /list - Get users list with pagination and filters
 * - GET /get/:id - Get user details by ID
 * - PUT /update-status - Update user status
 * - DELETE /delete/:id - Delete user
 */
class UserMasterService {
  /**
   * @name getList
   * @description Gets a paginated list of users with optional filters
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Number of records per page (default: 10)
   * @param {string} params.search - Search term for email/full name
   * @param {string} params.status - User status filter (active/inactive/suspend)
   * @param {string} params.admin_status - Admin status filter (active/inactive)
   * @param {string} params.signup_type - Signup type filter (normal/google/apple)
   * @param {string} params.sort_by - Sort field (created_at, email, full_name)
   * @param {string} params.order_by - Sort order (asc/desc)
   * @throws {Error} If the request fails
   * @returns {Promise<Object>} The response data containing users list and pagination
   */
  static getList(params = {}) {
    return new Promise(async (resolve, reject) => {
      try {
        // Let axios handle the query string building so encryption interceptor can work
        const data = await axios.get(user.list, { params });
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name get
   * @description Gets user details by ID
   * @param {string|number} id - User ID
   * @throws {Error} If the request fails
   * @returns {Promise<Object>} The response data containing user details
   */
  static get(id) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await axios.get(`${user.get}/${id}`);
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name updateStatus
   * @description Updates user status (admin status or user status) using UUID
   * @param {Object} request - The request object containing update data
   * @param {string} request.id - User UUID
   * @param {string} request.type - Status type ('admin' or 'user')
   * @param {string} request.status - New status value ('active' or 'deactivated')
   * @param {string} request.reason - Reason for deactivation (optional)
   * @throws {Error} If the request fails
   * @returns {Promise<Object>} The response data
   */
  static updateStatus(request) {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, type, status, reason } = request;
        
        // Build the URL with UUID parameter
        const url = `${user.updateStatus}?uuid=${id}`;
        console.log('Update Status URL:', url);
        console.log('Request data:', { id, type, status, reason });
        
        // Prepare request payload based on the new API format
        const payload = {};
        
        if (type === 'admin') {
          payload.admin_status = status;
          if (reason) {
            payload.admin_deactivate_reason = reason;
          }
        } else if (type === 'user') {
          payload.is_active = status === 'active';
        }
        
        console.log('Payload:', payload);
        
        const data = await axios.put(url, payload);
        resolve(data.data);
      } catch (err) {
        console.error('Update Status Error:', err);
        reject(err);
      }
    });
  }

  /**
   * @name getAdminUser
   * @description Gets admin user details by UUID
   * @param {string} uuid - User UUID
   * @throws {Error} If the request fails
   * @returns {Promise<Object>} The response data containing admin user details with profile information
   */
  static getAdminUser(uuid) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await axios.get(`${user.getAdminUser}?uuid=${uuid}`);
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name grantPremiumReward
   * @description Grants the configured number of months of free premium to the given users.
   * @param {string[]} uuids - User UUIDs to grant the reward to
   * @throws {Error} If the request fails (e.g. months-to-grant isn't configured yet)
   * @returns {Promise<Object>} The response data, whose `data` includes { granted_count, months }
   */
  static grantPremiumReward(uuids) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await axios.post(user.grantPremiumReward, { user_uuids: uuids });
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name delete
   * @description Deletes a user (same lock-then-purge flow as the user deleting their own account)
   * @param {string} id - User UUID
   * @param {string} [reason] - Optional reason, included in the email to the user
   * @throws {Error} If the request fails
   * @returns {Promise<Object>} The response data
   */
  static delete(id, reason) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await axios.delete(`${user.deleteAccount}?uuid=${id}`, {
          data: reason ? { reason } : {},
        });
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name restrict
   * @description Restricts a user (read-only mode)
   * @param {Object} request
   * @param {string} request.id - User UUID
   * @param {string} request.reason - Reason shown to the user
   * @param {string} [request.end_date] - ISO end date, omit when untilManual
   * @param {boolean} [request.until_manual] - Restrict until lifted manually
   * @returns {Promise<Object>} The response data
   */
  static restrict({ id, reason, end_date, until_manual }) {
    return new Promise(async (resolve, reject) => {
      try {
        const payload = { reason };
        if (until_manual) payload.until_manual = true;
        else payload.end_date = end_date;
        const data = await axios.put(`${user.restrict}?uuid=${id}`, payload);
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name liftRestriction
   * @description Lifts a restriction immediately
   * @param {string} id - User UUID
   * @returns {Promise<Object>} The response data
   */
  static liftRestriction(id) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await axios.put(`${user.liftRestriction}?uuid=${id}`, {});
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }
}

export default UserMasterService;
